import { history as historyRouter } from 'instantsearch.js/es/lib/routers';
import type { UiState } from 'instantsearch.js';
import { algoliaAttributes } from 'assets/constants';
import cloneDeep from 'lodash/cloneDeep';

type RouteState = {
  query?: string;
  page?: string;
  city?: string[];
  cityCode?: string[];
  medicalServices?: string[];
  name?: string[];
  sortBy?: string;
  hitsPerPage?: string;
  loc?: string | undefined | null;
  r?: number | "all" | undefined;
};

const routeStateDefaultValues: RouteState = {
  query: '',
  page: '1',
  city: undefined,
  cityCode: undefined,
  medicalServices: undefined,
  name: undefined,
  sortBy: 'instant_search',
  hitsPerPage: '20',
  loc: undefined,
  r: 10000,
};

const router = historyRouter<RouteState>({

  createURL({ qsModule, routeState, location }): string {
    const { protocol, hostname, port = '', pathname, hash } = location;
    const portWithPrefix = port === '' ? '' : `:${port}`;
    const urlParts = location.href.match(/^(.*?)\/search/);
    const baseUrl = (urlParts && urlParts[0]) || `${protocol}//${hostname}${portWithPrefix}${pathname}`;

    const queryParameters: Partial<RouteState> = {};

    if (
      routeState.query &&
      routeState.query !== routeStateDefaultValues.query
    ) {
      queryParameters.query = encodeURIComponent(routeState.query);
    }
    if (routeState.page && routeState.page !== routeStateDefaultValues.page) {
      queryParameters.page = routeState.page;
    }


    if (routeState.name && routeState.name !== routeStateDefaultValues.name) {
      queryParameters.name = routeState.name.map(encodeURIComponent);
    }

    if (
      routeState.city &&
      routeState.city !== routeStateDefaultValues.city
    ) {
      queryParameters.city = routeState.city.map(encodeURIComponent);
    }

    if (
      routeState.cityCode &&
      routeState.cityCode !== routeStateDefaultValues.cityCode
    ) {
      queryParameters.cityCode = routeState.cityCode;
    }
    if (
      routeState.medicalServices &&
      routeState.medicalServices !== routeStateDefaultValues.medicalServices
    ) {
      queryParameters.medicalServices = routeState.medicalServices.map(encodeURIComponent);
    }

    if (
      routeState.sortBy &&
      routeState.sortBy !== routeStateDefaultValues.sortBy
    ) {
      queryParameters.sortBy = routeState.sortBy;
    }
    if (
      routeState.hitsPerPage &&
      routeState.hitsPerPage !== routeStateDefaultValues.hitsPerPage
    ) {
      queryParameters.hitsPerPage = routeState.hitsPerPage;
    }
    if (
      routeState.loc &&
      routeState.loc !== routeStateDefaultValues.loc
    ) {
      queryParameters.loc = routeState.loc;
      queryParameters.r = routeState.r;
    }

    const queryString = qsModule.stringify(queryParameters, {
      addQueryPrefix: true,
      arrayFormat: 'repeat',
    });

    return `${baseUrl}${queryString}${hash}`;
  },

  parseURL({ qsModule, location }): RouteState {
    const queryParameters = qsModule.parse(location.search.slice(1));
    const {
      query = '',
      page = 1,
      city,
      cityCode,
      medicalServices,
      name,
      hitsPerPage,
      sortBy,
      loc,
      r
    } = queryParameters;


    const allcityCode = (
      Array.isArray(cityCode) ? cityCode : [cityCode].filter(Boolean)
    ) as string[];

    const allmedicalServicesCode = (
      Array.isArray(medicalServices) ? medicalServices : [medicalServices].filter(Boolean)
    ) as string[];

    const allName = (
      Array.isArray(name) ? name : [name].filter(Boolean)
    ) as string[];

    const allcities = (
      Array.isArray(city) ? city : [city].filter(Boolean)
    ) as string[];

    return {
      query: decodeURIComponent(query as string),
      page: page as string,
      city: allcities.map(decodeURIComponent),
      cityCode: allcityCode.map(decodeURIComponent),
      medicalServices: allmedicalServicesCode.map(decodeURIComponent),
      name: allName.map(decodeURIComponent),
      sortBy: sortBy as string,
      hitsPerPage: hitsPerPage as string,
      loc: decodeURIComponent(loc as string),
      r: r ? parseInt(r as string) : 10000,
    };
  },
});

const getStateMapping = ({ indexName }: { indexName: string }) => ({
  stateToRoute(uiState: UiState): RouteState {
    const indexUiState = uiState[indexName];
    const exactUiState = uiState['exact'];
    let aroundRadiusMatches;
    let arroundLatLngMatches;
    let cityCodesMatches;
    if (exactUiState != undefined) {
      aroundRadiusMatches = exactUiState?.configure?.aroundRadius;
      arroundLatLngMatches = exactUiState?.configure?.aroundLatLng;
      cityCodesMatches = exactUiState?.refinementList && exactUiState?.refinementList[algoliaAttributes.medicalCityCode];
    }
    else {
      aroundRadiusMatches = indexUiState?.configure?.aroundRadius;
      arroundLatLngMatches = indexUiState?.configure?.aroundLatLng;
      cityCodesMatches = indexUiState?.refinementList && indexUiState?.refinementList[algoliaAttributes.medicalCityCode];
    }

    return {
      query: indexUiState.query,
      page: (indexUiState.page && String(indexUiState.page)) || undefined,
      city: indexUiState.refinementList && indexUiState.refinementList['city'],
      cityCode: cityCodesMatches,
      medicalServices: indexUiState.refinementList && indexUiState.refinementList['medicalServices'],
      name: indexUiState.refinementList && indexUiState.refinementList[algoliaAttributes.Name],
      sortBy: indexUiState.sortBy,
      hitsPerPage: (indexUiState.hitsPerPage && String(indexUiState.hitsPerPage)) || undefined,
      loc: arroundLatLngMatches ? arroundLatLngMatches : undefined,
      r: aroundRadiusMatches ? aroundRadiusMatches : undefined,
    };
  },

  routeToState(routeState: RouteState): UiState {

    const refinementList: { [key: string]: string[] } = {};
    let filters = 'objectType:OuterService ';
    let aroundLatLng = undefined;
    let aroundRadius = undefined;
    let configure = {};
    let geoSearch = false;
    let geofilters = '';
    let exactfilters = '';
    let geoconfigure = {};
    let exactconfigure = {};

    if (routeState.city) {
      refinementList['city'] = routeState.city;
    }

    if (routeState.medicalServices && routeState.medicalServices.length) {
      const params = new URLSearchParams(window.location.search);
      const medicalServicesArray = params.getAll("medicalServices");
      if (medicalServicesArray.length > 1) {
        refinementList.medicalServices = medicalServicesArray.map(decodeURIComponent);
      }
      else {
        refinementList['medicalServices'] = routeState.medicalServices[0].split(',');
      }
    }

    if (routeState.name && routeState.name.length) {
      refinementList.name = routeState.name[0].split(',');
    }

    if (routeState.loc && routeState.loc !== 'undefined') {
      geoSearch = true;
      aroundLatLng = routeState.loc;
      aroundRadius = routeState.r;
    }
    else {
      geoSearch = false;
    }

    if (routeState.cityCode && routeState.cityCode.length) {
      const citiesCodes = routeState.cityCode[0].split(',');
      const citiesFilter = citiesCodes.map((cityCode) => {
        return `cityCode:${cityCode}`;
      });
      refinementList[algoliaAttributes.medicalCityCode] = citiesCodes;
      if (geoSearch) {
        exactfilters = geofilters = filters;
        exactfilters += filters ? ` AND ${citiesFilter.join(' OR ')}` : `${citiesFilter.join(' OR ')}`;
        geofilters += filters ? ` AND NOT ${citiesFilter.join(' OR ')}` : `NOT ${citiesFilter.join(' OR ')}`;
      }
      else {
        filters += filters ? ` AND (${citiesFilter.join(' OR ')})` : `(${citiesFilter.join(' OR ')})`;
      }
    }

    if (filters) {
      configure = { ...configure, filters };
    }
    if (aroundLatLng) {
      configure = { ...configure, aroundLatLng, aroundRadius };
    }
    if (geofilters) {
      geoconfigure = { ...geoconfigure, filters: geofilters };
      exactconfigure = { ...exactconfigure, filters: exactfilters };
    }
    if (aroundLatLng) {
      exactconfigure = { ...exactconfigure, aroundLatLng, aroundRadius };
      geoconfigure = { ...geoconfigure, aroundLatLng, aroundRadius, getRankingInfo: true };
    }

    if (geoSearch) {
      let modifiedRefList = cloneDeep(refinementList);
      delete modifiedRefList[algoliaAttributes.medicalCityCode];
      return {
        [indexName]: {
          query: routeState.query,
          page: Number(routeState.page),
          refinementList: modifiedRefList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: configure ? configure : undefined,
        },
        ['exact']: {
          query: routeState.query,
          page: Number(routeState.page),
          refinementList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: exactconfigure ? exactconfigure : undefined,
        },
        ['geo']: {
          query: routeState.query,
          page: Number(routeState.page),
          refinementList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: geoconfigure ? geoconfigure : undefined,
        },
      };
    }
    else {
      return {
        [indexName]: {
          query: routeState.query,
          page: Number(routeState.page),
          refinementList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: configure ? configure : undefined,
        }
      };
    }
  },
});

const getRouting = (indexName: string) => ({
  router,
  stateMapping: getStateMapping({ indexName }),
});

export default getRouting;